import styled, { css } from 'styled-components'

import {
  Box,
  Text,
  TruncateWithTooltip,
  Tooltip,
  Caption,
  Button,
  ButtonUnstyled,
  LinkUnstyled,
  Markdown as BaseMarkdown,
} from '@sketch/components'
import { linkStyles, breakpoint } from '@sketch/global-styles'

import { ReactComponent as CheckIcon } from '@sketch/icons/check-16'
import { ReactComponent as ExclamationMarkIcon } from '@sketch/icons/exclamation-mark-circle-11'
import { ReactComponent as ArrowDownCircle } from '@sketch/icons/arrow-down-circle-16'
import { ReactComponent as BellIcon } from '@sketch/icons/bell-24'
import { ReactComponent as BellStrikeThroughIcon } from '@sketch/icons/bell-strikethrough-24'
import { ReactComponent as StarIcon } from '@sketch/icons/star-16'

export const MarkNotificationAsReadButton = styled(Button).attrs({
  size: '32',
})`
  padding: 4px 20px;
`

export const SubscriptionButton = styled(MarkNotificationAsReadButton).attrs({
  size: '32',
})`
  margin-left: 12px;
`

// Notification structure
export const NotificationContainer = styled(LinkUnstyled)<{
  $isRead?: boolean
}>(
  ({ theme, $isRead }) => css`
    display: flex;
    align-items: flex-start;

    padding: 16px;

    margin-left: ${!$isRead ? '24px' : undefined};

    white-space: pre-wrap;
    color: inherit;

    position: relative;
    transition: background ${theme.transitions.duration[1]}
      ${theme.transitions.timing.easeOut};

    &:nth-child(even) {
      background-color: ${theme.colors.background.secondary.B};
    }

    ${breakpoint('xs')`
      margin-left: ${!$isRead ? '24px' : undefined};
      padding: 16px 16px 16px 0;
      border-radius: ${theme.radii.xlarge};
    `}

    /* stylelint-disable-next-line no-descending-specificity */
    &:hover,
    [data-testid='notifications-read-title'] ~ &:hover {
      background: ${theme.colors.state.hover};

      ${MarkAsReadButtonContainer} {
        opacity: 1;
      }
    }
  `
)

export const NotificationContainerMobile = styled(NotificationContainer)<{
  $isRead?: boolean
}>(
  () => css`
    margin-left: 0;

    ${breakpoint('xs')`
        padding: 16px;
        margin-left: 0;
    `}

    /* stylelint-disable-next-line no-descending-specificity */
    ${MarkAsReadButtonContainer} {
      padding: 8px;
      align-self: end;
    }
  `
)

export const NotificationFlex = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 100%;
`

export const NotificationFlexMobile = styled(NotificationFlex)`
  flex-direction: column;
`

export const NotificationItemContainer = styled.div`
  min-width: 0;
  margin-left: ${({ theme }) => theme.space[4]}px;
  display: flex;
  flex: 1;
  flex-direction: column;
`

export const NotificationItemContainerMobile = styled(
  NotificationItemContainer
)(
  () => css`
    width: 100%;
    margin-left: 0;

    ${NotificationBody} {
      align-items: flex-start;

      /* stylelint-disable-next-line no-descending-specificity */
      ${Box} {
        margin-top: 2px; /* stylelint-disable-line scales/space */
      }
    }
  `
)

export const NotificationShareName = styled(TruncateWithTooltip)(
  ({ theme }) => css`
    font-size: ${theme.fontSizes.C};
    color: ${theme.colors.foreground.secondary.D};
    font-weight: ${theme.fontWeights.regular};
  `
)

export const NotificationDescription = styled(Text)(
  ({ theme }) => css`
    margin: 0;
    font-size: ${theme.fontSizes.E};
    font-weight: ${theme.fontWeights.regular};
    line-height: 1.2;
    overflow-wrap: 'break-word';
  `
)

export const NotificationBody = styled(Box).attrs({
  mt: 1,
  mb: 0,
})(
  ({ theme }) => css`
    display: flex;
    flex: 1;

    align-items: center;

    font-weight: ${theme.fontWeights.regular};
    font-size: ${theme.fontSizes.E};
    color: ${theme.colors.foreground.secondary.B};
    overflow-wrap: break-word;
  `
)

// TODO: Remove updates-page-revamp FF
// https://github.com/sketch-hq/Cloud/issues/17965
export const NotificationsMeta = styled(Caption).attrs({
  mt: 1,
  mb: 0,
  ml: 2,
})(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.foreground.secondary.C};
    font-size: ${theme.fontSizes.D};
  `
)

export const NotificationsDate = styled(Caption).attrs({
  mt: 1,
  mb: 0,
  ml: 2,
})(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.foreground.secondary.C};
    font-size: ${theme.fontSizes.D};
    width: 180px;
  `
)

export const NotificationsDateMobile = styled(NotificationsDate)`
  text-align: right;
`

export const NotificationsType = styled(Box).attrs({
  mt: 1,
  mb: 0,
  ml: 2,
})(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.foreground.secondary.A};
    font-size: ${theme.fontSizes.D};
    width: 105px;
    font-weight: ${theme.fontWeights.regular};
  `
)

// Other styles
export const UnreadIndicator = styled.span`
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 8px;
    background: ${({ theme }) => theme.colors.sketch.A};
    width: 8px;
    height: 8px;
    margin-top: -4px;
    border-radius: 50%;
  }

  ${breakpoint('xs')`
    &:before {
      left: -16px;
    }
  `}

  ${breakpoint('md')`
    &:before {
      left: -24px;
    }
  `}

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary.B};
  }
`

export const MobileHeader = styled.div<{ $isRead: boolean }>(
  ({ $isRead }) => css`
    position: relative;
    width: 100%;

    ${UnreadIndicator} {
      &:before {
        top: 12px;
        left: 0;
      }
    }

    /* stylelint-disable-next-line no-descending-specificity */
    ${NotificationsType} {
      margin-left: ${$isRead ? 0 : 16}px;
      display: inline;
    }
  `
)

export const FailedAsset = styled.div`
  color: ${({ theme }) => theme.colors.state.negative.A};
`

export const MarkAsReadButtonContainer = styled.div`
  margin-left: 8px;

  ${breakpoint('md')`
    width: 58px;
    display: flex;
    justify-content: flex-end;
    margin-left: 0;
  `}

  @media (hover: hover) {
    opacity: 0;
  }
`

const iconCSS = css`
  width: 22px;
  height: 22px;
  margin: -8px;
  flex: 0 0 auto;
`

export const MarkAsReadIcon = styled(CheckIcon)`
  ${iconCSS}
`

export const SubscribeIcon = styled(BellIcon)`
  ${iconCSS}
`

export const UnsubscribeIcon = styled(BellStrikeThroughIcon)`
  ${iconCSS}
`

export const StarredIcon = styled(StarIcon)`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const FailedIcon = styled(ExclamationMarkIcon)`
  width: 11px;
  height: 11px;
  flex: 0 0 auto;
`

export const AssetReadyIcon = styled(ArrowDownCircle)`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  margin-top: 2px; /* stylelint-disable-line scales/space */
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const TooltipAvatar = styled(Tooltip)`
  display: flex;
`

export const DownloadAsset = styled(ButtonUnstyled)(
  ({ theme }) => css`
    ${linkStyles};
    text-decoration: underline;
    font-weight: ${theme.fontWeights.semibold};
  `
)

export const Markdown = styled(BaseMarkdown)`
  overflow: hidden;
`

export const Skeleton = styled(NotificationContainer)(
  ({ theme }) => css`
    width: calc(100% - 24px);
    height: 72px;
    border-radius: ${theme.radii.xlarge};

    ::after {
      /* Have some dummy text to fill the height */
      content: '_';
      opacity: 0;
    }
  `
)
